import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

function MiniPriceChart({ productId, days = 7 }) {
  const [chartData, setChartData] = useState([]);
  const [minPriceInfo, setMinPriceInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/prices/history/${productId}?days=${days}`);
        if (!res.ok) return;

        const data = await res.json();
        
        if (!Array.isArray(data) || data.length === 0) {
          setChartData([]);
          setMinPriceInfo(null);
          return;
        }

        // Hole die letzten n Datenpunkte für den Mini-Chart basierend auf days
        const dataPoints = Math.min(days, data.length);
        const recentData = data.slice(-dataPoints).map(item => {
          const date = item.date ? new Date(item.date) : new Date();
          return {
            minPrice: parseFloat(item.minPrice),
            date: date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
            fullDate: date
          };
        });

        // Finde den niedrigsten Preis und das zugehörige Datum
        let lowestPrice = Infinity;
        let lowestPriceData = null;
        
        recentData.forEach(item => {
          if (item.minPrice < lowestPrice) {
            lowestPrice = item.minPrice;
            lowestPriceData = item;
          }
        });

        setChartData(recentData);
        setMinPriceInfo(lowestPriceData);
      } catch (err) {
        console.error('Fehler beim Laden des Mini-Charts:', err);
      }
    };

    fetchData();
  }, [productId, days]);

  if (!chartData || chartData.length === 0) {
    return <div style={{ width: '100%', height: '30px' }} />;
  }

  // Custom Tooltip Komponente
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: 'rgba(15, 42, 53, 0.95)',
          border: '1px solid rgba(0, 212, 255, 0.5)',
          borderRadius: '6px',
          padding: '8px 12px',
          color: '#00ff88',
          fontSize: '13px',
          fontWeight: '600',
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
        }}>
          €{data.minPrice.toFixed(2)} · {data.date}
        </div>
      );
    }
    return null;
  };

  // Bestimme Trend: ist Preis gestiegen oder gefallen?
  const firstPrice = chartData[0].minPrice;
  const lastPrice = chartData[chartData.length - 1].minPrice;
  const isTrendingUp = lastPrice > firstPrice;
  const lineColor = isTrendingUp ? '#ef4444' : '#10b981'; // Rot für Preis gestiegen, Grün für gefallen

  return (
    <div style={{ width: '100%', height: '30px', position: 'relative' }} title={minPriceInfo ? `Niedrigster: €${minPriceInfo.minPrice.toFixed(2)} (${minPriceInfo.date})` : ''}>
      <ResponsiveContainer width="100%" height={30}>
        <LineChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(0, 212, 255, 0.3)' }} />
          <Line
            type="monotone"
            dataKey="minPrice"
            stroke={lineColor}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default MiniPriceChart;
